import { memo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { flushSync } from 'react-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col } from 'styled-bootstrap-grid';

import type { GeneratedTranslationForm } from '@/services/ws-genai/src/main/resources/rest-api';
import { generateTranslations } from '@/services/ws-genai/src/main/resources/rest-api';
import type { TranslationDto, TranslationForm } from '@/services/ws-translate/src/main/resources/rest-api';
import { addTranslation, getNamespaces, Language, updateTranslation } from '@/services/ws-translate/src/main/resources/rest-api';
import { CreatableSelectField, TextField } from '@/js/libs/form';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { ModalStateOpen } from '@/js/libs/hooks/useModalState';
import { useSubscribe } from '@/js/libs/observable';
import { Button, FormGroup, Icons, InnerGroup, Label, Modal, Notification } from '@/js/libs/ui/src/V2';
import { NotActive, useLazyRequest, useRequest } from '@/js/libs/use-request';

import { useSuccessNotification } from 'src/hooks/useSuccessNotification.ts';
import { getDefaultValues, getSubmitValues, languageToFieldName } from './helpers.ts';
import type { FormValues, SubmitValues } from './schema.ts';
import { useSchema } from './schema.ts';
import { FieldWrapper } from './styled.ts';

type Props = {
  state: ModalStateOpen<TranslationDto | undefined>;
  onCreated?: () => void;
  onUpdated?: (translation: TranslationDto) => void;
  isClone?: boolean;
};

export const TranslationModal = memo(function TranslationModal({ state, onCreated, onUpdated, isClone }: Props) {
  const translation = state.payload;
  const [t] = useTranslation(['backend', 'common', 'adminLocalization', 'messages']);
  const [generatingLanguage, setGeneratingLanguage] = useState<Language | null>(null);
  const isEdit = !!translation?.id && !isClone;

  const schema = useSchema();
  const form = useForm<FormValues, unknown, SubmitValues>({
    defaultValues: getDefaultValues(state.payload, isClone),
    resolver: zodResolver(schema),
  });
  const {
    formState: { isSubmitting },
    handleSubmit,
    getValues,
    setValue,
    control,
  } = form;
  const keyField = useWatch({ control, name: 'key' });
  const namespaceField = useWatch({ control, name: 'namespace' });
  const namespaceWarnings = new Map<string, { severity: 'warning' | 'error'; message: string }>([
    ['common', { severity: 'warning', message: t('adminLocalization:COMMON_NAMESPACE_WARNING') }],
    ['enums', { severity: 'warning', message: t('adminLocalization:ENUMS_NAMESPACE_WARNING') }],
    ['messages', { severity: 'warning', message: t('adminLocalization:MESSAGES_NAMESPACE_WARNING') }],
    ['backend', { severity: 'error', message: t('adminLocalization:BACKEND_NAMESPACE_WARNING') }],
  ]);
  const namespaceWarning = isEdit && namespaceField ? namespaceWarnings.get(namespaceField.value) : undefined;
  const isCriticalNamespace = namespaceWarning?.severity === 'error';

  const namespacesRequest = useRequest({
    apiMethod: getNamespaces,
    params: isEdit ? NotActive : {},
  });
  const namespaceOptions = useSubscribe(namespacesRequest.data$, (r) => r?.data.map((item) => ({ label: item, value: item })) ?? []);
  const namespacesLoading = useSubscribe(namespacesRequest.pending$);

  const createRequest = useLazyRequest({
    apiMethod: addTranslation,
    onSuccess: useSuccessNotification(t('messages:TRANSLATION_WAS_CREATED')),
  });

  const updateRequest = useLazyRequest({
    apiMethod: updateTranslation,
    onSuccess: useSuccessNotification(t('messages:TRANSLATION_WAS_UPDATED')),
  });

  const createOrUpdateTranslation = useCallbackRef((translationForm: TranslationForm) => {
    if (translation?.id && !isClone) {
      return updateRequest.load({ id: translation.id, form: translationForm });
    }
    return createRequest.load({ translation: translationForm });
  });

  const onSubmit = useCallbackRef(async (values: SubmitValues) => {
    const submitValues = getSubmitValues(values, isClone ? undefined : translation?.pageRef);
    try {
      const { data } = await createOrUpdateTranslation(submitValues);
      if (isEdit) {
        onUpdated?.(data);
      } else {
        onCreated?.();
      }
      state.close();
    } catch {
      // failure is surfaced by the global onFail handler
    }
  });

  const generateTranslationsRequest = useLazyRequest({
    apiMethod: generateTranslations,
  });
  const generatingTranslations = useSubscribe(generateTranslationsRequest.pending$);
  const generatingTranslationsForAll = generatingTranslations && generatingLanguage === null;

  const handleGenerateTranslationsClick = useCallbackRef(async () => {
    const formValues = getValues();

    try {
      const { data } = await generateTranslationsRequest.load({
        form: {
          key: formValues.key,
          valueEn: formValues.valueEn,
          valueDe: formValues.valueDe,
          valueFr: formValues.valueFr,
          valueIt: formValues.valueIt,
        },
      });

      setValue('valueEn', data.valueEn, { shouldValidate: true });
      setValue('valueDe', data.valueDe, { shouldValidate: true });
      setValue('valueFr', data.valueFr, { shouldValidate: true });
      setValue('valueIt', data.valueIt, { shouldValidate: true });
    } catch {
      // failure is surfaced by the global onFail handler
    }
  });

  const generateSingleTranslation = useCallbackRef(async (language: Language) => {
    flushSync(() => {
      setGeneratingLanguage(language);
    });
    const fieldName = languageToFieldName(language);
    const { key, valueEn, valueDe, valueFr, valueIt } = getValues();
    const payload: GeneratedTranslationForm = {
      key,
      valueEn,
      valueDe,
      valueFr,
      valueIt,
      [fieldName]: '',
    };

    try {
      const { data } = await generateTranslationsRequest.load({ form: payload });
      if (data[fieldName]) {
        setValue(fieldName, data[fieldName], { shouldValidate: true });
      }
    } catch {
      // failure is surfaced by the global onFail handler
    } finally {
      setGeneratingLanguage(null);
    }
  });

  const generateEn = useCallbackRef(() => generateSingleTranslation(Language.EN));
  const generateDe = useCallbackRef(() => generateSingleTranslation(Language.DE));
  const generateFr = useCallbackRef(() => generateSingleTranslation(Language.FR));
  const generateIt = useCallbackRef(() => generateSingleTranslation(Language.IT));

  return (
    <Modal
      state={state}
      title={
        isClone
          ? t('adminLocalization:CLONE_TRANSLATION')
          : isEdit
            ? t('adminLocalization:EDIT_TRANSLATION')
            : t('adminLocalization:ADD_TRANSLATION')
      }>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <FormGroup>
              <Col md={6} data-testid='namespace-field'>
                {isEdit ? (
                  <TextField disabled required control={control} name='namespace.value' label={t('adminLocalization:NAMESPACE')} />
                ) : (
                  <CreatableSelectField
                    control={control}
                    name='namespace'
                    label={t('adminLocalization:NAMESPACE')}
                    options={namespaceOptions}
                    isLoading={namespacesLoading}
                    isClearable
                    required
                  />
                )}
              </Col>
              <Col md={6} data-testid='key-field'>
                <TextField
                  control={control}
                  name='key'
                  label={t('common:KEY')}
                  placeholder={t('common:ENTER')}
                  disabled={isEdit}
                  required
                />
              </Col>
            </FormGroup>
            {namespaceWarning && (
              <InnerGroup data-testid='namespace-warning'>
                <Notification fullWidth uncloseable type={namespaceWarning.severity} body={namespaceWarning.message} />
              </InnerGroup>
            )}
            {!isClone && (
              <>
                <InnerGroup data-testid='english-field'>
                  <Label>{t('common:ENGLISH')}</Label>
                  <FieldWrapper>
                    <div>
                      <TextField
                        control={control}
                        name='valueEn'
                        disabled={generatingTranslationsForAll || generatingLanguage === Language.EN}
                        placeholder={t('common:ENTER')}
                        required
                      />
                    </div>
                    <Button onClick={generateEn} isLoading={generatingLanguage === Language.EN} disabled={!keyField} secondary iconOnly>
                      <Icons.Change />
                    </Button>
                  </FieldWrapper>
                </InnerGroup>
                <InnerGroup data-testid='german-field'>
                  <Label>{t('common:GERMAN')}</Label>
                  <FieldWrapper>
                    <div>
                      <TextField
                        control={control}
                        name='valueDe'
                        disabled={generatingTranslationsForAll || generatingLanguage === Language.DE}
                        placeholder={t('common:ENTER')}
                        required
                      />
                    </div>
                    <Button onClick={generateDe} isLoading={generatingLanguage === Language.DE} disabled={!keyField} secondary iconOnly>
                      <Icons.Change />
                    </Button>
                  </FieldWrapper>
                </InnerGroup>
                <InnerGroup data-testid='french-field'>
                  <Label>{t('common:FRENCH')}</Label>
                  <FieldWrapper>
                    <div>
                      <TextField
                        control={control}
                        name='valueFr'
                        disabled={generatingTranslationsForAll || generatingLanguage === Language.FR}
                        placeholder={t('common:ENTER')}
                        required
                      />
                    </div>
                    <Button onClick={generateFr} isLoading={generatingLanguage === Language.FR} disabled={!keyField} secondary iconOnly>
                      <Icons.Change />
                    </Button>
                  </FieldWrapper>
                </InnerGroup>
                <InnerGroup data-testid='italian-field'>
                  <Label>{t('common:ITALIAN')}</Label>
                  <FieldWrapper>
                    <div>
                      <TextField
                        control={control}
                        name='valueIt'
                        disabled={generatingTranslationsForAll || generatingLanguage === Language.IT}
                        placeholder={t('common:ENTER')}
                        required
                      />
                    </div>
                    <Button onClick={generateIt} isLoading={generatingLanguage === Language.IT} disabled={!keyField} secondary iconOnly>
                      <Icons.Change />
                    </Button>
                  </FieldWrapper>
                </InnerGroup>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={state.close} disabled={isSubmitting} ghost dataTestId='cancel-button'>
              {t('common:CANCEL')}
            </Button>
            {!isClone && (
              <Button onClick={handleGenerateTranslationsClick} isLoading={generatingTranslationsForAll} disabled={!keyField} secondary>
                <Icons.AiText />
                {t('adminLocalization:GENERATE_TRANSLATIONS')}
              </Button>
            )}
            <Button
              primary={!isCriticalNamespace}
              critical={isCriticalNamespace}
              type='submit'
              isLoading={isSubmitting || generatingTranslationsForAll}
              dataTestId='submit-button'>
              {isClone ? t('common:CLONE') : isEdit ? t('common:SAVE') : t('common:ADD')}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
});
